/**
 * Dust Swap Hook
 *
 * Main hook for swapping low-value tokens ("dust") to SOL via Jupiter aggregator.
 * Only works on mainnet since Jupiter doesn't support devnet.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getConnection } from '@/lib/solana/anchorClient';
import {
  getJupiterQuotes,
  serializeInstructions,
  batchSwapResults,
  isMainnet,
  calculateEstimatedSolOutput,
  type SwappableToken,
  type JupiterQuoteResult,
} from '@/lib/solana/jupiter';
import { signAndSendLegacyTransaction, signAndSendMultipleVersionedTransactions } from '@/lib/solana/swapTransaction';
import { checkSwapOrderStatus } from '@/lib/solana/swapOrderCheck';
import * as swapApi from '@/api/swap';
import { useAuth } from '@/hooks/useAuth';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { sumTokenAccountRentSol } from '@/utils/solana/rentEstimator';
import { captureException, createErrorContext } from '@/lib/sentry';
import { parseSolanaError } from '@/utils/solanaErrorParser';

// Maximum tokens per swap transaction (Jupiter swaps are compute-intensive)
const MAX_TOKENS_PER_BATCH = 1;

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;


/**
 * Sleep utility
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Processing step for dust swap operation
 */
export type DustSwapStep = 'quoting' | 'signing' | 'sending' | 'confirming';

/**
 * Progress callback for dust swap UI updates
 */
export type DustSwapProgressCallback = (
  step: DustSwapStep,
  details: {
    currentTokenIndex?: number;
    totalTokens?: number;
    currentTokenSymbol?: string;
    attemptCount?: number;
    estimatedSolOutput?: number;
  }
) => void;

/**
 * Input for dust swap operation
 */
export interface DustSwapInput {
  tokens: SwappableToken[];
  onProgress?: DustSwapProgressCallback;
}

/**
 * Result of dust swap operation
 */
export interface DustSwapResult {
  /** First transaction signature (for compatibility) */
  signature: string;
  /** All transaction signatures */
  signatures: string[];
  /** Number of tokens successfully swapped */
  swappedTokens: number;
  /** SOL received from Jupiter swap (in SOL, not lamports) */
  estimatedSolFromSwap: number;
  /** SOL received from closing token accounts / rent reclaim (in SOL, not lamports) */
  estimatedSolFromRent: number;
  /** Total estimated SOL received (swap + rent) */
  estimatedSolReceived: number;
  /** Mints that couldn't be swapped (no route, not tradable) */
  nonSwappableTokens: string[];
  /** Mints that need retry (rate limited, network error) */
  retryableTokens: string[];
}

/**
 * Error thrown when tokens need to be retried
 */
export class RetryableTokensError extends Error {
  constructor(
    public retryableTokens: string[],
    public nonSwappableTokens: string[]
  ) {
    super('Some tokens are temporarily unavailable. Please try again in a moment.');
    this.name = 'RetryableTokensError';
  }
}

/**
 * Error thrown when trying to swap on devnet
 */
export class DevnetNotSupportedError extends Error {
  constructor() {
    super('Dust swapping is only available on mainnet. Jupiter does not support devnet.');
    this.name = 'DevnetNotSupportedError';
  }
}

/**
 * Dust swap mutation function
 */
async function dustSwap(
  tokens: SwappableToken[],
  walletAddress: string,
  walletProvider: any,
  onProgress?: DustSwapProgressCallback
): Promise<DustSwapResult> {
  const flowStartTime = performance.now();
  console.log('[DUST-SWAP] ============================================');
  console.log('[DUST-SWAP] Starting dust swap for', tokens.length, 'tokens');
  console.log('[DUST-SWAP] Wallet address:', walletAddress);
  console.log('[DUST-SWAP] Token details:', tokens.map(t => ({
    symbol: t.symbol,
    mint: t.mint,
    balance: t.balance,
    tokenAccount: t.tokenAccount,
  })));
  console.log('[DUST-SWAP] isMainnet():', isMainnet());

  const errorContext = createErrorContext('dust_swap', walletAddress, {
    tokensCount: tokens.length,
    tokenMints: tokens.map((t) => t.mint),
  });

  // Step 1: Check if we're on mainnet
  if (!isMainnet()) {
    console.error('[DUST-SWAP] Attempted swap on devnet - isMainnet() returned false');
    throw new DevnetNotSupportedError();
  }

  if (tokens.length === 0) {
    throw new Error('No tokens to swap');
  }

  // Get connection early - needed for Jupiter Ultra instruction extraction
  const connection = getConnection();

  // Step 2: Get Jupiter Ultra quotes for all tokens
  console.log('[DUST-SWAP] Fetching Jupiter Ultra quotes...');
  onProgress?.('quoting', { totalTokens: tokens.length });

  const { swappable, retryable, nonSwappable } = await getJupiterQuotes(
    tokens,
    walletAddress,
    connection
  );

  console.log(
    `[DUST-SWAP] Quote results: ${swappable.length} swappable, ${retryable.length} retryable, ${nonSwappable.length} non-swappable`
  );

  // Step 3: If ANY tokens are retryable, don't proceed - user must retry all
  if (retryable.length > 0) {
    console.warn('[DUST-SWAP] Found retryable tokens, aborting swap');
    throw new RetryableTokensError(
      retryable.map((r) => r.mint),
      nonSwappable.map((r) => r.mint)
    );
  }

  // Step 4: If no swappable tokens, return early
  if (swappable.length === 0) {
    console.warn('[DUST-SWAP] No swappable tokens found');
    return {
      signature: '',
      signatures: [],
      swappedTokens: 0,
      estimatedSolFromSwap: 0,
      estimatedSolFromRent: 0,
      estimatedSolReceived: 0,
      nonSwappableTokens: nonSwappable.map((r) => r.mint),
      retryableTokens: [],
    };
  }

  // Calculate estimated SOL output from swap
  const estimatedSolFromSwap = calculateEstimatedSolOutput(swappable);
  // Estimated SOL from rent reclaim: the live deposit of each token account that
  // wrapUpSwapOrder will close (rent minimums vary by account age under SIMD-0437)
  const lamportsByMint = new Map(tokens.map((t) => [t.mint, t.lamports]));
  const rentSolFor = (results: JupiterQuoteResult[]) =>
    sumTokenAccountRentSol(results.map((r) => ({ lamports: lamportsByMint.get(r.mint) })));
  const estimatedSolFromRent = rentSolFor(swappable);
  // Total estimated SOL received
  const estimatedSolReceived = estimatedSolFromSwap + estimatedSolFromRent;
  console.log(`[DUST-SWAP] Estimated SOL output: ${estimatedSolReceived.toFixed(6)} SOL`);

  // Step 5: Batch swappable tokens (max 1 per transaction due to compute limits)
  const batches = batchSwapResults(swappable, MAX_TOKENS_PER_BATCH);
  console.log(`[DUST-SWAP] Created ${batches.length} batches`);

  const signatures: string[] = [];
  const failedMints: string[] = [];
  const nonSwappableMints: string[] = nonSwappable.map((r) => r.mint);

  // Step 6: Prepare all transactions upfront (create orders + get signatures)
  console.log('[DUST-SWAP] === Phase 1: Preparing all transactions ===');
  onProgress?.('quoting', { totalTokens: batches.length });

  interface PreparedSwap {
    batchIndex: number;
    orderId: string;
    serializedTx: string;
    batch: JupiterQuoteResult[];
  }

  const preparedSwaps: PreparedSwap[] = [];

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const currentTokenSymbol = batch[0]?.symbol || `Token ${i + 1}`;
    console.log(`[DUST-SWAP] Preparing batch ${i + 1}/${batches.length} (${currentTokenSymbol})...`);

    let orderId: string | undefined;
    let retries = 0;

    // Step 6a: Build serialized instructions for backend
    const serializedInstructions = batch.flatMap((result) =>
      serializeInstructions(result.swapInstructions!)
    );

    const addressLookupTableAddresses = [
      ...new Set(batch.flatMap((r) => r.addressLookupTableAddresses)),
    ];

    const swapRequest: swapApi.SwapOrderRequest = {
      tokenAccounts: batch.map((r) => r.tokenAccount),
      tokenSwappedAmounts: batch.map((r) => r.balance),
      addressLookupTableAccounts: addressLookupTableAddresses,
      instructions: serializedInstructions,
    };

    // Step 6b: Create swap order with retry
    while (retries <= MAX_RETRIES) {
      try {
        const orderResponse = await swapApi.createSwapOrder(swapRequest);

        if (orderResponse.code !== 0 || !orderResponse.data) {
          if (
            orderResponse.message?.includes('System busy') ||
            orderResponse.message?.includes('busy')
          ) {
            throw new Error(orderResponse.message);
          }
          throw new Error(orderResponse.message || 'Failed to create swap order');
        }

        orderId = orderResponse.data;
        console.log(`  [DUST-SWAP] Created order: ${orderId}`);
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable =
          errorMsg.includes('System busy') ||
          errorMsg.includes('busy') ||
          errorMsg.includes('500');

        if (isRetryable && retries < MAX_RETRIES) {
          const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
          console.warn(`  [DUST-SWAP] System busy, retrying in ${delayMs}ms`);
          await sleep(delayMs);
          retries++;
        } else {
          console.error(`  [DUST-SWAP] Failed to create order for batch ${i + 1}:`, error);
          captureException(error, {
            ...errorContext,
            stage: 'create_swap_order',
            batchIndex: i + 1,
            batchCount: batches.length,
          });
          throw error;
        }
      }
    }

    if (!orderId) {
      throw new Error(`Failed to create swap order after ${MAX_RETRIES} retries`);
    }

    // Step 6c: Get partially signed transaction from backend
    retries = 0;
    let serializedTx: string | undefined;

    while (retries <= MAX_RETRIES) {
      try {
        const signatureResponse = await swapApi.generateSwapSignature(orderId);

        if (signatureResponse.code !== 0 || !signatureResponse.data) {
          throw new Error(signatureResponse.message || 'Failed to get signature');
        }

        serializedTx = signatureResponse.data.serialized_tx;
        console.log(`  [DUST-SWAP] Got transaction for order: ${orderId}`);
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);

        // Check for transaction size errors - these tokens can't be swapped
        const isTransactionTooLarge =
          errorMsg.includes('Versioned Transaction Too Large') ||
          errorMsg.includes('Transaction too large') ||
          errorMsg.includes('transaction too large');

        if (isTransactionTooLarge) {
          console.warn(`  [DUST-SWAP] Transaction too large, marking as non-swappable`);
          const batchMints = batch.map((r) => r.mint);
          failedMints.push(...batchMints);
          nonSwappableMints.push(...batchMints);
          break;
        }

        const isRetryable =
          errorMsg.includes('500') ||
          errorMsg.includes('Internal server error') ||
          errorMsg.includes('busy');

        if (isRetryable && retries < MAX_RETRIES) {
          const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
          await sleep(delayMs);
          retries++;
        } else {
          console.error(`  [DUST-SWAP] Failed to get signature for batch ${i + 1}:`, error);
          captureException(error, {
            ...errorContext,
            stage: 'get_swap_signature',
            batchIndex: i + 1,
            orderId,
          });
          throw error;
        }
      }
    }

    // Skip if transaction was too large
    if (!serializedTx) {
      continue;
    }

    // Check if order was already processed
    const orderStatus = await checkSwapOrderStatus(connection, orderId);
    if (orderStatus.exists && orderStatus.isUsed) {
      console.log(`[DUST-SWAP] Order ${orderId} already executed, skipping`);
      signatures.push(`already-processed:${orderId}`);
      continue;
    }

    preparedSwaps.push({
      batchIndex: i,
      orderId,
      serializedTx,
      batch,
    });
  }

  // Step 7: Sign all transactions at once (single wallet popup)
  if (preparedSwaps.length === 0) {
    console.log('[DUST-SWAP] No transactions to sign');
  } else {
    console.log(`[DUST-SWAP] === Phase 2: Signing ${preparedSwaps.length} transactions ===`);
    onProgress?.('signing', {
      totalTokens: preparedSwaps.length,
      estimatedSolOutput: estimatedSolReceived,
    });

    const serializedTxs = preparedSwaps.map((p) => p.serializedTx);

    try {
      // Sign and send all transactions with single wallet approval
      const txSignatures = await signAndSendMultipleVersionedTransactions(
        serializedTxs,
        walletProvider,
        connection
      );

      console.log(`[DUST-SWAP] All ${txSignatures.length} swap transactions confirmed`);
      signatures.push(...txSignatures);

      // Step 8: Send wrapUp transactions for each successful swap
      // Note: wrapUp failures are non-critical - swaps already succeeded
      console.log('[DUST-SWAP] === Phase 3: Processing wrapUp transactions ===');
      onProgress?.('confirming', {
        totalTokens: preparedSwaps.length,
        estimatedSolOutput: estimatedSolReceived,
      });

      for (let i = 0; i < preparedSwaps.length; i++) {
        const { orderId, batch } = preparedSwaps[i];
        const tokenSymbol = batch[0]?.symbol || `Token ${i + 1}`;

        try {
          const wrapUpResponse = await swapApi.generateWrapUpSignature(orderId);

          if (wrapUpResponse.code !== 0 || !wrapUpResponse.data) {
            console.warn(`  [DUST-SWAP] WrapUp failed for ${tokenSymbol} (non-critical)`);
            continue;
          }

          const wrapUpTx = wrapUpResponse.data.serialized_tx;
          const wrapUpSignature = await signAndSendLegacyTransaction(
            wrapUpTx,
            walletProvider,
            connection
          );
          console.log(`  [DUST-SWAP] WrapUp confirmed for ${tokenSymbol}: ${wrapUpSignature}`);
        } catch (wrapUpError: any) {
          // WrapUp failure is non-critical - swap already succeeded
          console.warn(`  [DUST-SWAP] WrapUp failed for ${tokenSymbol}:`, wrapUpError.message);
          captureException(wrapUpError, {
            ...errorContext,
            stage: 'wrapup_transaction',
            orderId,
          });
        }
      }
    } catch (error: any) {
      console.error('[DUST-SWAP] Batch signing/sending failed:', error);
      captureException(error, {
        ...errorContext,
        stage: 'batch_sign_and_send',
        preparedCount: preparedSwaps.length,
      });
      throw error;
    }
  }

  const totalTime = performance.now() - flowStartTime;
  console.log(`[DUST-SWAP] Completed in ${totalTime.toFixed(2)}ms`);

  // Calculate actual swapped count (exclude failed mints)
  const actualSwappedTokens = swappable.length - failedMints.length;

  // Recalculate SOL amounts based on actual swapped tokens
  // For simplicity, we prorate the swap amount and recalculate rent for actual tokens
  const actualSolFromSwap = actualSwappedTokens > 0
    ? (estimatedSolFromSwap * actualSwappedTokens) / swappable.length
    : 0;
  const failedMintSet = new Set(failedMints);
  const actualSolFromRent = rentSolFor(swappable.filter((r) => !failedMintSet.has(r.mint)));
  const actualSolReceived = actualSolFromSwap + actualSolFromRent;

  console.log(`[DUST-SWAP] Swapped ${actualSwappedTokens} tokens:`);
  console.log(`  - From swap: ~${actualSolFromSwap.toFixed(6)} SOL`);
  console.log(`  - From rent: ~${actualSolFromRent.toFixed(6)} SOL`);
  console.log(`  - Total: ~${actualSolReceived.toFixed(6)} SOL`);

  return {
    signature: signatures[0] || '',
    signatures,
    swappedTokens: actualSwappedTokens,
    estimatedSolFromSwap: actualSolFromSwap,
    estimatedSolFromRent: actualSolFromRent,
    estimatedSolReceived: actualSolReceived,
    nonSwappableTokens: nonSwappableMints,
    retryableTokens: [],
  };
}

/**
 * Hook for dust swapping tokens to SOL
 *
 * @example
 * ```tsx
 * const { mutate: swapDust, isPending, error } = useDustSwap();
 *
 * const handleSwap = () => {
 *   swapDust({ tokens: selectedTokens }, {
 *     onSuccess: (result) => {
 *       console.log(`Swapped ${result.swappedTokens} tokens for ~${result.estimatedSolReceived} SOL`);
 *       if (result.nonSwappableTokens.length > 0) {
 *         // Offer to burn non-swappable tokens instead
 *       }
 *     },
 *     onError: (error) => {
 *       if (error instanceof RetryableTokensError) {
 *         // Show retry message
 *       }
 *     }
 *   });
 * };
 * ```
 */
export function useDustSwap() {
  const { address, walletProvider } = useWalletAddress();
  const { isAuthenticated, authenticate } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DustSwapInput) => {
      if (!address) {
        throw new Error('Wallet not connected');
      }
      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }

      // Check authentication before proceeding
      if (!isAuthenticated) {
        console.log('[useDustSwap] User not authenticated, prompting authentication...');
        const authenticated = await authenticate();
        if (!authenticated) {
          throw new Error(
            'Authentication required. Please sign the message to verify your wallet ownership.'
          );
        }
      }

      return dustSwap(input.tokens, address, walletProvider, input.onProgress);
    },
    onSuccess: (data) => {
      console.log('[useDustSwap] Swap successful:', data);

      // Invalidate user progress to refresh balance
      queryClient.invalidateQueries({ queryKey: ['userProgress', address] });

      // Invalidate history to show new swap transaction
      queryClient.invalidateQueries({ queryKey: ['solHistory', address] });

      // Invalidate points queries to show earned points in history
      // Note: Backend needs time to sync and process the on-chain transaction
      // before points appear in the ledger, so we invalidate multiple times
      const invalidatePointsQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['points', 'balance'] });
        queryClient.invalidateQueries({ queryKey: ['points', 'ledger'] });
        queryClient.invalidateQueries({ queryKey: ['userPoints', address] });
      };

      // Immediate invalidation
      invalidatePointsQueries();

      // Delayed invalidations to catch backend sync (typically 3-10 seconds)
      setTimeout(invalidatePointsQueries, 3000);
      setTimeout(invalidatePointsQueries, 8000);
      setTimeout(invalidatePointsQueries, 15000);

      // Show success message with breakdown
      if (data.swappedTokens > 0) {
        const tokenText = data.swappedTokens > 1 ? 'tokens' : 'token';
        const swapSol = data.estimatedSolFromSwap.toFixed(6);
        const rentSol = data.estimatedSolFromRent.toFixed(6);
        const totalSol = data.estimatedSolReceived.toFixed(6);

        toast.success('Dust swap successful!', {
          description: `Swapped ${data.swappedTokens} ${tokenText} for ${totalSol} SOL (${swapSol} swap + ${rentSol} rent)`,
        });
      }

      // Notify about non-swappable tokens
      if (data.nonSwappableTokens.length > 0) {
        toast.info(`${data.nonSwappableTokens.length} token(s) couldn't be swapped`, {
          description: 'These tokens can be burned instead.',
          duration: 5000,
        });
      }
    },
    onError: (error: Error, variables: DustSwapInput) => {
      console.error('[useDustSwap] Swap failed:', error);

      captureException(error, {
        operation: 'dust_swap_mutation',
        tokensCount: variables.tokens.length,
        tokenMints: variables.tokens.map((t) => t.mint),
        errorName: error.name,
        errorMessage: error.message,
      });

      // Handle specific error types
      if (error instanceof DevnetNotSupportedError) {
        toast.error('Not available on devnet', {
          description: 'Dust swapping only works on mainnet.',
        });
      } else if (error instanceof RetryableTokensError) {
        toast.warning('Some tokens temporarily unavailable', {
          description: 'Please try again in a few seconds. All tokens will be re-quoted.',
          duration: 5000,
        });
      } else {
        // Parse the error for a user-friendly message
        const parsedError = parseSolanaError(error);

        // Don't show toast for user cancellations
        if (error.message?.includes('User rejected') || error.message?.includes('cancelled')) {
          return;
        }

        // Handle slippage errors with a user-friendly warning
        if (parsedError.type === 'slippage_exceeded') {
          toast.warning('Price changed during swap', {
            description: 'The token price moved too quickly. Please try again to get the latest price.',
            duration: 5000,
          });
          return;
        }

        toast.error(parsedError.isRetryable ? 'Please try again' : 'Swap failed', {
          description: parsedError.suggestedAction || parsedError.message,
        });
      }
    },
  });
}
